// src/components/BizCreateModal.jsx
import React, { useState } from 'react';
import { X, MapPin } from 'lucide-react';

export function BizCreateModal({ isOpen, onClose, onSave, isSaving }) {
  // 🌟 upsertBiz에 매핑되는 모든 필드 추가
  const [formData, setFormData] = useState({
    bizRegNo: '',       // 사업자등록번호
    bizMgtNo: '',       // 사업장관리번호
    bizStartNo: '',     // 사업장개시번호
    bizName: '',        // 사업장명
    ceoName: '',        // 대표자명
    zipcode: '',        // 우편번호
    address1: '',       // 소재지
    address2: '',       // 상세주소
    businessType: '',   // 업종
    employeeCount: '',  // 상시근로자수
    bizPhone: '',       // 사업장전화번호
    faxPhone: '',       // 팩스번호
    regBranch: '서울광역본부', // 등록지사
    currBranch: '',     // 현관할지사
    managerName: '',    // 담당자명
    managerPhone: '',   // 담당자휴대폰
    managerEmail: '',   // 담당자이메일
    closeYn: 'N',       // 휴폐업여부 (N: 정상, Y: 폐업)
    estabDate: '',      // 성립일 (YYYYMMDD)
    closeDate: ''       // 소멸일 (YYYYMMDD)
  });

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openAddressSearch = () => {
    if (window.daum && window.daum.Postcode) {
      new window.daum.Postcode({
        oncomplete: function(data) {
          setFormData(prev => ({
            ...prev,
            zipcode: data.zonecode,
            address1: data.address
          }));
        }
      }).open();
    } else {
      alert("주소 검색 스크립트를 로드할 수 없습니다.");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.bizRegNo || !formData.bizName) {
      return alert("사업자등록번호와 사업장명은 필수입니다.");
    }
    
    // 빈 문자열인 숫자 데이터는 null 처리 등 필요한 전처리 수행
    const payload = {
      ...formData,
      employeeCount: formData.employeeCount ? parseInt(formData.employeeCount, 10) : 0
    };
    
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl rounded-xl bg-white shadow-xl flex flex-col max-h-[90vh]">
        
        {/* 모달 헤더 */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-100 px-6 py-4">
          <h2 className="text-lg font-bold text-slate-800">사업장 신규 등록</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* 모달 바디 (스크롤 영역) */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            
            {/* --- 1. 기본 정보 --- */}
            <div className="col-span-2 mb-2 border-b pb-2">
              <h3 className="text-sm font-bold text-slate-800">1. 기본 정보</h3>
            </div>
            
            <div className="col-span-1">
              <label className="mb-1 block text-sm font-medium text-slate-700">사업자등록번호 *</label>
              <input type="text" name="bizRegNo" value={formData.bizRegNo} onChange={handleChange} required placeholder="(- 제외)"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
            </div>
            <div className="col-span-1">
              <label className="mb-1 block text-sm font-medium text-slate-700">사업장명 *</label>
              <input type="text" name="bizName" value={formData.bizName} onChange={handleChange} required
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
            </div>
            <div className="col-span-1">
              <label className="mb-1 block text-sm font-medium text-slate-700">사업장관리번호 *</label>
              <input type="text" name="bizMgtNo" value={formData.bizMgtNo} onChange={handleChange} required
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
            </div>
            <div className="col-span-1">
              <label className="mb-1 block text-sm font-medium text-slate-700">사업장개시번호</label>
              <input type="text" name="bizStartNo" value={formData.bizStartNo} onChange={handleChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
            </div>
            <div className="col-span-1">
              <label className="mb-1 block text-sm font-medium text-slate-700">대표자명</label>
              <input type="text" name="ceoName" value={formData.ceoName} onChange={handleChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
            </div>
            <div className="col-span-1">
              <label className="mb-1 block text-sm font-medium text-slate-700">상시근로자수</label>
              <input type="number" name="employeeCount" value={formData.employeeCount} onChange={handleChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
            </div>
            <div className="col-span-1">
              <label className="mb-1 block text-sm font-medium text-slate-700">업종</label>
              <input type="text" name="businessType" value={formData.businessType} onChange={handleChange} placeholder="예: 건설업, 제조업"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
            </div>

            {/* 🌟 주소 입력 영역을 MemberFormModal 스타일로 변경 */}
            <div className="col-span-2 pt-2 border-t border-slate-100 mt-2">
              <label className="block text-xs font-semibold text-slate-500 mb-2">주소 정보</label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input 
                    type="text" name="zipcode" readOnly value={formData.zipcode} placeholder="우편번호" 
                    className="h-10 w-28 rounded-lg border border-slate-300 bg-slate-50 px-3 text-sm focus:outline-none" 
                  />
                  <button 
                    type="button" onClick={openAddressSearch} 
                    className="flex items-center gap-1.5 h-10 px-3 rounded-lg border border-blue-200 bg-blue-50 text-blue-600 text-sm font-medium hover:bg-blue-100 transition-colors"
                  >
                    <MapPin className="w-4 h-4" /> 주소 검색
                  </button>
                </div>
                <input 
                  type="text" name="address1" readOnly value={formData.address1} placeholder="기본 주소 (주소 검색을 이용하세요)" 
                  className="h-10 w-full rounded-lg border border-slate-300 bg-slate-50 px-3 text-sm focus:outline-none" 
                />
                <input 
                  type="text" name="address2" value={formData.address2} onChange={handleChange} placeholder="상세 주소를 입력하세요 (본사, 1동 등)" 
                  className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" 
                />
              </div>
            </div>

            {/* --- 2. 담당자 및 연락처 --- */}
            <div className="col-span-2 mt-4 mb-2 border-b pb-2">
              <h3 className="text-sm font-bold text-slate-800">2. 담당자 및 연락처 정보</h3>
            </div>
            
            <div className="col-span-1">
              <label className="mb-1 block text-sm font-medium text-slate-700">사업장 전화번호</label>
              <input type="text" name="bizPhone" value={formData.bizPhone} onChange={handleChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
            </div>
            <div className="col-span-1">
              <label className="mb-1 block text-sm font-medium text-slate-700">사업장 FAX</label>
              <input type="text" name="faxPhone" value={formData.faxPhone} onChange={handleChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
            </div>
            <div className="col-span-1">
              <label className="mb-1 block text-sm font-medium text-slate-700">담당자명</label>
              <input type="text" name="managerName" value={formData.managerName} onChange={handleChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
            </div>
            <div className="col-span-1">
              <label className="mb-1 block text-sm font-medium text-slate-700">담당자 휴대폰</label>
              <input type="text" name="managerPhone" value={formData.managerPhone} onChange={handleChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
            </div>
            <div className="col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-700">담당자 이메일</label>
              <input type="email" name="managerEmail" value={formData.managerEmail} onChange={handleChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
            </div>

            {/* --- 3. 관리 정보 --- */}
            <div className="col-span-2 mt-4 mb-2 border-b pb-2">
              <h3 className="text-sm font-bold text-slate-800">3. 관리 정보</h3>
            </div>

            <div className="col-span-1">
              <label className="mb-1 block text-sm font-medium text-slate-700">등록지사</label>
              <select name="regBranch" value={formData.regBranch} onChange={handleChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none">
                <option value="서울광역본부">서울광역본부</option>
                <option value="강원지역본부">강원지역본부</option>
                <option value="부산광역본부">부산광역본부</option>
              </select>
            </div>
            <div className="col-span-1">
              <label className="mb-1 block text-sm font-medium text-slate-700">현관할지사</label>
              <input type="text" name="currBranch" value={formData.currBranch} onChange={handleChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
            </div>
            <div className="col-span-1">
              <label className="mb-1 block text-sm font-medium text-slate-700">상태 (정상/폐업)</label>
              <select name="closeYn" value={formData.closeYn} onChange={handleChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none">
                <option value="N">정상 (N)</option>
                <option value="Y">폐업/소멸 (Y)</option>
              </select>
            </div>
            <div className="col-span-1"></div>
            <div className="col-span-1">
              <label className="mb-1 block text-sm font-medium text-slate-700">성립일 (YYYYMMDD)</label>
              <input type="text" name="estabDate" value={formData.estabDate} onChange={handleChange} maxLength={8} placeholder="예: 20240101"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
            </div>
            <div className="col-span-1">
              <label className="mb-1 block text-sm font-medium text-slate-700">소멸일 (YYYYMMDD)</label>
              <input type="text" name="closeDate" value={formData.closeDate} onChange={handleChange} maxLength={8}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
            </div>
          </div>

          {/* 모달 푸터 (버튼 고정 영역) */}
          <div className="mt-8 flex justify-end gap-2 border-t border-slate-100 pt-4">
            <button type="button" onClick={onClose} className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 transition-colors">
              취소
            </button>
            <button type="submit" disabled={isSaving} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {isSaving ? '저장 중...' : '등록하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}